<template>
  <div class="tab-content">
    <div class="tab-pane fade show active">
      <div
        v-if="hasDummyThematicProcessedData"
        class="subscribe-container text-center"
      >
        <img src="@/assets/images/lock.svg" />
        <span class="locked-message d-block mb-2 fw-bold">
          Subscribe now to unlock the full list of Prime Funds recommendations
        </span>
        <el-button
          type="primary"
          class="btn-color v-btn"
          @click="onSubscribeClick"
        >
          Subscribe now
        </el-button>
      </div>
      <el-table
        :data="currentThematicFundsProcessed"
        border
        style="width: 100%"
        :row-class-name="getRowClassName"
        class="custom-scrollable-table header-aggressive"
        :default-sort="defaultSort"
        scrollbar-always-on
      >
        <el-table-column
          label="Active"
          :width="columnWidth"
          :fixed="isSmallScreen ? 'left' : false"
        >
          <template #header>
            <div style="margin-top: 5px">
              <img
                src="@/assets/images/active.svg"
                alt="Active"
                class="header-icon"
              />
              Thematic fund name &nbsp;<img
                src="@/assets/images/circle.svg"
                @click="openDialog('Thematic fund name', content1)"
                style="cursor: pointer; margin-bottom: 2px"
              />
            </div>
          </template>

          <template #default="scope">
            <span class="d-flex gap-data prime-funds">
              <div v-if="Number(scope.row.newFlag) === 1" class="new-tag">
                New
              </div>
              <el-tooltip
                :content="
                  Number(scope.row.inWatchlist) === 1
                    ? 'Remove from Watchlist'
                    : 'Add to Watchlist'
                "
                placement="top"
              >
                <img
                  :src="
                    Number(scope.row.inWatchlist) === 1 ? eyeRemove : eyeAdd
                  "
                  alt="Watchlist Status"
                  @click="toggleWatchlist(scope.row)"
                  class="watchlist-icon"
                />
              </el-tooltip>
              <div>
                <div
                  class="watchlist-container"
                  v-if="scope.row.showStatusMsg && scope.row.statusMessage"
                >
                  <div class="watchlist-status-overlay gap-data">
                    <img :src="eye" />
                    <span class="watchlist-status-message">
                      {{ scope.row.statusMessage }}
                    </span>
                  </div>
                </div>
                <div v-else>
                  <span class="alert-message">
                    {{ scope.row.alertMessage }}
                  </span>
                </div>
              </div>
              <a
                :href="`${baseUrl}/scheme-details/?schemecode=${scope.row.schemeCode}`"
                target="_blank"
                class="fund-name text-decoration-none"
              >
                {{ scope.row.name }}
              </a>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Theme" width="150">
          <template #default="scope">
            <span style="padding-left: 26px; display: inline-block">
              {{ scope.row.theme }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="oneYearReturn"
          label="1 year"
          width="100"
          :sortable="!isTrialExistCurrentThematicFunds"
          :class="{ 'no-sort-icon': isTrialExistCurrentThematicFunds }"
          :sort-method="
            (rowA, rowB) => sortByProperty(rowA, rowB, 'oneYearReturn')
          "
        >
          <template #default="scope">
            <span>{{
              scope.row.oneYearReturn === "0"
                ? "NA"
                : scope.row.oneYearReturn + "%"
            }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="threeYearCAGR"
          label="3 years"
          width="100"
          :sortable="!isTrialExistCurrentThematicFunds"
          :class="{ 'no-sort-icon': isTrialExistCurrentThematicFunds }"
          :sort-method="
            (rowA, rowB) => sortByProperty(rowA, rowB, 'threeYearCAGR')
          "
        >
          <template #header>
            <el-tooltip
              content="Returns over 1 year are annualised"
              placement="top"
              popper-class="custom-tooltip"
            >
              <span>3 years</span>
            </el-tooltip>
          </template>
          <template #default="scope">
            <span>{{
              scope.row.threeYearCAGR === "0"
                ? "NA"
                : scope.row.threeYearCAGR + "%"
            }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="fiveYearCAGR"
          label="5 years"
          width="100"
          :sortable="!isTrialExistCurrentThematicFunds"
          :class="{ 'no-sort-icon': isTrialExistCurrentThematicFunds }"
          :sort-method="
            (rowA, rowB) => sortByProperty(rowA, rowB, 'fiveYearCAGR')
          "
          style="border-bottom: none !important"
        >
          <template #header>
            <el-tooltip
              content="Returns over 1 year are annualised"
              placement="top"
              popper-class="custom-tooltip"
            >
              <span>5 years</span>
            </el-tooltip>
          </template>
          <template #default="scope">
            <span style="border-bottom: none !important">{{
              scope.row.fiveYearCAGR === "0"
                ? "NA"
                : scope.row.fiveYearCAGR + "%"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="report" label="Reports" width="100">
          <template #default="scope">
            <span>
              <a
                href="#"
                @click.prevent="
                  fetchReport(scope.row.schemeCode, scope.row.name)
                "
                style="color: #01a2d9; cursor: pointer"
              >
                View report
              </a>
            </span>
          </template>
        </el-table-column>
      </el-table>
      <br />
      <div
        @click="toggleTableVisibility"
        class="past-calls-card"
        :class="{
          'rounded-all': !isTableVisible,
          'rounded-top': isTableVisible,
        }"
      >
        <span style="font-size: larger; margin-left: 8px"
          >Past thematic calls</span
        >
        <span class="arrow-icon" :class="{ rotated: isTableVisible }">
          &#9660;
        </span>
      </div>

      <el-table
        v-if="isTableVisible"
        :data="sortedActivities"
        border
        style="width: 100%"
        scrollbar-always-on
        class="custom-scrollable-table past-table header-aggressive"
      >
        <el-table-column
          label="Active"
          :width="columnWidth"
          :fixed="isSmallScreen ? 'left' : false"
        >
          <template #header>
            <div style="margin-top: 5px">
              <img
                src="@/assets/images/active.svg"
                alt="Active"
                class="header-icon"
              />
              Fund Name
            </div>
          </template>

          <template #default="scope">
            <span class="d-flex gap-data prime-funds" style="color: #918d99">
              {{ scope.row.schemeDetails }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Theme" width="150">
          <template #default="scope">
            <span style="padding-left: 26px; display: inline-block">
              {{ scope.row.thematic }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="recoDate" label="Reco date" width="100">
          <template #default="scope">
            <span>
              <div class="fw-bold" style="color: #1fd19f">
                {{ scope.row.entryReco.split("|")[0] }}
              </div>
              <div>{{ scope.row.entryReco.split("|")[1] }}</div>
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="currentStatus"
          label="Current status"
          width="100"
        >
          <template #default="scope">
            <span>
              <div
                class="fw-bold"
                :style="{
                  color:
                    scope.row.exitReco.split('|')[0] === 'Sell'
                      ? '#DD2E44'
                      : scope.row.exitReco.split('|')[0] === 'Hold'
                        ? '#c5c550'
                        : '',
                }"
              >
                {{ scope.row.exitReco.split("|")[0] }}
              </div>

              <div>{{ scope.row.exitReco.split("|")[1] }}</div>
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="return" label="Returns" width="100">
          <template #default="scope">
            <span>{{
              scope.row.return === "0" ? "NA" : scope.row.return + "%"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="reports" label="Reports" width="100">
          <template #default="scope">
            <div v-if="getReportUrls(scope.row.reports).buyUrl">
              <a
                :href="getReportUrls(scope.row.reports).buyUrl"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #01a2d9"
              >
                Buy Report
              </a>
            </div>
            <div v-if="getReportUrls(scope.row.reports).holdUrl">
              <a
                :href="getReportUrls(scope.row.reports).holdUrl"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #01a2d9"
              >
                Hold Report
              </a>
            </div>
            <div v-if="getReportUrls(scope.row.reports).sellUrl">
              <a
                :href="getReportUrls(scope.row.reports).sellUrl"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #01a2d9"
              >
                Sell Report
              </a>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <el-dialog
        v-model="isDialogVisible"
        class="fw-bold dialog-color"
        :title="dialogTitle"
        :width="popupwidth"
      >
        <p>{{ dialogContent }}</p>
        <span slot="footer" class="dialog-footer">
          <el-button @click="isDialogVisible = false" class="v-btn btn-color"
            >Close</el-button
          >
        </span>
      </el-dialog>
      <el-dialog
        v-model="isPopupVisible"
        class="dialog-color"
        :title="fundTitle"
        :width="popupwidth"
      >
        <b-card class="shadow-sm">
          <b-row>
            <b-col class="text-center popup-width">
              <div>
                {{
                  reportData.scheme_call === "Buy"
                    ? "Buy Reco Date"
                    : "Hold Reco Date"
                }}
              </div>
              <div class="font-weight-bold text-align-center">
                {{ formatDate(reportData.date_of_call) || "N/A" }}
              </div>
            </b-col>

            <b-col class="text-center popup-width">
              <div>Latest Report</div>
              <div>
                <a
                  class="text-align-center underline"
                  v-if="reportData.report_url"
                  :href="`${baseUrl}${reportData.report_url}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="color: #00a3d9"
                >
                  View
                </a>
                <span v-else>--</span>
              </div>
            </b-col>

            <b-col class="text-center popup-width">
              <div>{{ displayText }}</div>
              <div
                class="text-align-center"
                :style="{
                  color: reportData.fundReturn >= 0 ? '#1FD19F' : 'red',
                  fontWeight: 'bold',
                }"
              >
                {{
                  reportData.fundReturn != null
                    ? `${Number(reportData.fundReturn).toFixed(2)}%`
                    : "--"
                }}
              </div>
            </b-col>
          </b-row>
        </b-card>
        <div class="past-activities mt-3" v-if="pastActivities.length >= 1">
          <h4 class="text-muted">Past activity of calls on the fund</h4>
          <div v-for="(activity, index) in pastActivities" :key="index">
            <b-card
              class="mb-2 shadow-sm"
              style="background: rgba(217, 217, 217, 0.25)"
            >
              <b-row>
                <b-col class="text-center popup-width">
                  <div class="text-muted">
                    {{
                      activity.scheme_call === "Buy"
                        ? "Buy Reco Date"
                        : "Hold Reco Date"
                    }}
                  </div>
                  <div class="text-muted">
                    {{ formatDate(activity.date_of_call) || "N/A" }}
                  </div>
                </b-col>

                <b-col class="text-center popup-width">
                  <div class="text-muted">Latest Report</div>
                  <div>
                    <a
                      class="text-muted underline"
                      v-if="activity.report_url"
                      :href="`${baseUrl}${activity.report_url}`"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </a>
                    <span v-else>N/A</span>
                  </div>
                </b-col>

                <b-col class="text-center popup-width">
                  <div class="text-muted">{{ displayText }}</div>
                  <div class="text-muted">
                    {{
                      activity.fundReturn != null
                        ? `${Number(activity.fundReturn).toFixed(2)}%`
                        : "N/A"
                    }}
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, ref, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
const baseUrl = process.env.VUE_APP_BASE_URL;
const store = useStore();
const eyeAdd = require("@/assets/images/eyeAdd.svg");
const eyeRemove = require("@/assets/images/eyeRemove.svg");
const isSmallScreen = ref(false);
const columnWidth = computed(() => (isSmallScreen.value ? 250 : 350));
const currentThematicFunds = computed(() => store.getters.getThemeFunds);
const currentPastThematicFunds = computed(() => store.getters.getThematicFunds);
const checkScreenSize = () => {
  isSmallScreen.value = window.innerWidth <= 768;
};
const displayText = computed(() => {
  return window.innerWidth < 375 ? "Returns" : "Return since Reco";
});
const hasDummyThematicProcessedData = computed(() => {
  return currentThematicFundsProcessed.value.some((row) => row.isDummy);
});
function onSubscribeClick() {
  const baseUrl = process.env.VUE_APP_BASE_URL;
  const subscriptionUrl = `${baseUrl}/prime-pricing`;
  window.open(subscriptionUrl, "_blank");
}
const processData = (data) => {
  if (!Array.isArray(data)) {
    console.error("Data is not an array:", data);
    return [];
  }
  if (data.length === 0) {
    return [
      {
        name: "DSP Healthcare Fund(G)-Direct Plan",
        theme: "Category 1",
        oneYearReturn: "0%",
        threeYearCAGR: "0%",
        fiveYearCAGR: "0%",
        isDummy: true,
      },
      {
        name: "DSP Healthcare Fund(G)-Direct Plan",
        theme: "Healthcare",
        oneYearReturn: "0%",
        threeYearCAGR: "0",
        fiveYearCAGR: "0%",
        isDummy: true,
      },
      {
        name: "DSP Healthcare Fund(G)-Direct Plan",
        theme: "Healthcare",
        oneYearReturn: "0%",
        threeYearCAGR: "0",
        fiveYearCAGR: "0%",
        isDummy: true,
      },
      {
        name: "DSP Healthcare Fund(G)-Direct Plan",
        theme: "Healthcare",
        oneYearReturn: "0%",
        threeYearCAGR: "0",
        fiveYearCAGR: "0%",
        isDummy: true,
      },
      {
        name: "DSP Healthcare Fund(G)-Direct Plan",
        theme: "Healthcare",
        oneYearReturn: "0%",
        threeYearCAGR: "0",
        fiveYearCAGR: "0%",
        isDummy: true,
      },
    ];
  }

  const actualData = data.map((row) => ({
    ...row,
    isDummy: false,
  }));

  const trialExists = actualData.some((row) => row.trial === "1");

  if (trialExists) {
    const dummyRows = actualData.slice(0, 2).map((row) => ({
      ...row,
      isDummy: true,
    }));
    const repeatedDummyRows = [...dummyRows, ...dummyRows];
    return [...actualData, ...repeatedDummyRows];
  }

  return actualData.sort(
    (a, b) => (b.fiveYearCAGR || 0) - (a.fiveYearCAGR || 0)
  );
};

const checkTrialExists = (data) => {
  return data.some((row) => row.trial === "1");
};

const isTrialExistCurrentThematicFunds = computed(() =>
  checkTrialExists(currentThematicFunds.value)
);
const currentThematicFundsProcessed = computed(() => {
  return processData(currentThematicFunds.value);
});
const popupwidth = computed(() => (isSmallScreen.value ? "100%" : "50%"));
function sortByProperty(rowA, rowB, property) {
  const numA = parseFloat(rowA[property]);
  const numB = parseFloat(rowB[property]);

  if (isNaN(numA)) return 1;
  if (isNaN(numB)) return -1;

  return numA - numB;
}
const isDialogVisible = ref(false);
const sortedActivities = computed(() => {
  return [...currentPastThematicFunds.value].sort((a, b) => {
    const dateA = a.exitReco ? new Date(a.exitReco.split("|")[1]) : null;
    const dateB = b.exitReco ? new Date(b.exitReco.split("|")[1]) : null;
   
    return dateB - dateA;
  });
});
import axios from "axios";
const reportData = ref({});
const pastActivities = ref([]);
const isPopupVisible = ref(false);
const fundTitle = ref("");
const fetchReport = async (schemeCode, fundName) => {
  if (!schemeCode) {
    return;
  }

  const url = `${baseUrl}/wp-admin/admin-ajax.php?action=prime_fund_active_theme&schemecode=${schemeCode}`;

  try {
    const response = await axios.get(url);

    if (response.data && response.data.length > 0) {
      reportData.value = response.data[0];
      pastActivities.value = response.data.slice(1);
      fundTitle.value = fundName;
      isPopupVisible.value = true;
    } else {
      console.warn("No report data found.");
    }
  } catch (error) {
    console.error("Error fetching report:", error);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const options = { day: "numeric", month: "short", year: "2-digit" };
  return date.toLocaleDateString("en-GB", options).replace(",", "");
};

const dialogContent = ref("");
const dialogTitle = ref("");
const content1 = `These funds invest along specific themes, strategies, sectors, or geographies. They suit only discerning, high-risk investors and need timed exits`;

const getReportUrls = (researchReport) => {
  if (!researchReport || typeof researchReport !== "string") {
    return { buyUrl: "", holdUrl: "", sellUrl: "" };
  }

  const reports = researchReport.split("|").map((item) => {
    // Split only on the first "-" to separate label from URL (URLs may contain hyphens)
    const firstDashIndex = item.indexOf("-");
    if (firstDashIndex === -1) {
      return { label: "", url: "" };
    }
    const label = item.substring(0, firstDashIndex).trim();
    const url = item.substring(firstDashIndex + 1).replace(/\\/g, "").trim();
    return { label, url };
  });
  const baseUrl = process.env.VUE_APP_BASE_URL;
  let buyUrl = "";
  let holdUrl = "";
  let sellUrl = "";
  reports.forEach((report) => {
    // Ensure proper URL construction without double slashes
    const baseUrlClean = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const reportUrlClean = report.url.startsWith("/") ? report.url : `/${report.url}`;
    const fullUrl = `${baseUrlClean}${reportUrlClean}`;
    if (report.url.includes("no_report")) {
      return;
    }
    if (report.label === "Buy") {
      buyUrl = fullUrl;
    } else if (report.label === "Hold") {
      holdUrl = fullUrl;
    } else if (report.label === "Sell") {
      sellUrl = fullUrl;
    }
  });
  return {
    buyUrl,
    holdUrl,
    sellUrl,
  };
};

const isTableVisible = ref(false);

const toggleTableVisibility = () => {
  isTableVisible.value = !isTableVisible.value;
};

const openDialog = (title, content) => {
  dialogTitle.value = title;
  dialogContent.value = content;
  isDialogVisible.value = true;
};
const getRowClassName = (row) => {
  const isDummy = row.row.isDummy === true;
  return isDummy ? "blur-row" : "";
};
onBeforeUnmount(() => {
  window.removeEventListener("resize", checkScreenSize);
});
onMounted(() => {
  store.dispatch("fetchFunds");
  store.dispatch("fetchThematicFunds");
  checkScreenSize();
  window.addEventListener("resize", checkScreenSize);
});

const toggleWatchlist = async (fund) => {
  fund.statusMessage = "";
  fund.showStatusMsg = true;
  let success;
  if (Number(fund.inWatchlist) === 1) {
    fund.statusMessage = "Removing from Watchlist...";
    success = await store.dispatch("removemffromwatchlist", fund.schemeCode);
    if (success === true) {
      fund.inWatchlist = 0;
      fund.statusMessage = "Removed from Watchlist";
    } else {
      alert("Failed to remove from watchlist. Please try again.");
      fund.showStatusMsg = false;
    }
  } else {
    fund.statusMessage = "Adding to Watchlist...";
    success = await store.dispatch("saveMfWatchlist", fund.schemeCode);
    if (success === true) {
      fund.inWatchlist = 1;
      fund.statusMessage = "Added to Watchlist";
    } else {
      alert("Failed to add to watchlist. Please try again.");
      fund.showStatusMsg = false;
    }
  }
  setTimeout(() => {
    fund.showStatusMsg = false;
  }, 1000);
};
</script>

<style scoped>
::v-deep .blur-row {
  filter: blur(9px) !important;
}
.subscribe-container {
  z-index: 5;
  position: relative;
  top: 180px;
  margin-bottom: -35px;
  margin-top: -6%;
}

@media (max-width: 1200px) {
  .subscribe-container {
    top: 168px;
    margin-top: -8%;
  }
}

@media (min-width: 555px) and (max-width: 991px) {
  .subscribe-container {
    top: 200px;
    margin-top: -11%;
  }
}

@media (min-width: 450px) and (max-width: 554px) {
  .subscribe-container {
    top: 245px;
    margin-top: -18%;
  }
}

@media (max-width: 450px) {
  .subscribe-container {
    top: 232px;
    margin-top: -24%;
  }
}
@media (max-width: 450px) {
  .popup-width {
    padding-right: 0;
    padding-left: 0;
  }
}

.text-muted {
  font-size: 14px;
  color: #6c757d;
}

.font-weight-bold {
  font-size: 16px;
  font-weight: bold;
}

.rounded-all {
  border-radius: 10px;
}

.rounded-top {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.latest-report-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
}

.latest-report-item {
  display: flex;
  font-size: 14px;
  color: #333333;
  font-weight: 500;
}

.funds-popup {
  width: 16px;
  height: 16px;
  margin-right: 5px;
  cursor: pointer;
}

.fund-name {
  color: #00a3d9;
  font-weight: 600;
}

.past-calls-card {
  padding: 3px;
  border: 1px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background-color: #f5f5f5;
}

.arrow-icon {
  transition: transform 0.3s;
  margin-right: 7px;
}

.rotated {
  transform: rotate(180deg);
}

.prime-funds {
  position: relative;
}

::v-deep(.el-table .descending .sort-caret.descending) {
  border-top-color: white !important;
}

::v-deep(.el-table .ascending .sort-caret.ascending) {
  border-bottom-color: white !important;
}

::v-deep(.el-table .el-table__cell),
::v-deep(.el-table .cell) {
  padding: 0px !important;
}

.header-aggressive {
  --el-table-header-bg-color: #012e40;
}

.shadow-effect {
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.2);
}

.new-tag {
  position: absolute;
  top: -6px;
  left: -19px;
  background-color: red;
  color: white;
  font-size: 8px;
  padding: 2px 18px 0px 18px;
  z-index: 10;
  font-weight: bold;
  transform: rotate(-45deg);
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);
}

.btn-color {
  background: #00a3d9 !important;
  border-radius: 10px;
  color: white !important;
  padding: 20px;
  border-color: unset !important;
}

.gap-data {
  column-gap: 5px;
  padding: 10px;
  padding-left: 19px;
}

.custom-tabs .nav-link {
  border: 1px solid #ccc;
  border-radius: 0;
}

.watchlist-container td:first-child {
  position: relative;
}

.watchlist-status-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  pointer-events: none;
  background: linear-gradient(90deg, #012e40 0%, #ffffff 100%);
  padding: 13px !important;
}

.watchlist-status-message {
  font-size: 14px;
  color: white !important;
}

.watchlist-status-image {
  width: 100%;
  height: auto;
  background: linear-gradient(90deg, #012e40 0%, #ffffff 100%);
}

.header-icon {
  width: 45px;
  height: 40px;
  margin-bottom: 7px;
}

.custom-scrollable-table {
  --el-table-header-text-color: white;
  --el-table-header-font-weight: bold;
  --el-table-border-color: none;
  border: 1px solid black;
  position: relative;
  overflow: hidden;
  border-collapse: collapse;
}

@media (min-width: 769px) {
  .custom-scrollable-table {
    border-radius: 10px;
  }

  .past-table {
    border-top-right-radius: 0 !important;
    border-top-left-radius: 0 !important;
    border-bottom-right-radius: 10px !important;
    border-bottom-left-radius: 10px !important;
  }
}

::v-deep(.custom-scrollable-table tbody tr:last-child td) {
  border-bottom: none;
}

::v-deep(.custom-scrollable-table .el-table__header-wrapper thead tr th) {
  font-size: larger;
}

::v-deep(.custom-scrollable-table .el-table__header-wrapper thead tr th),
::v-deep(.custom-scrollable-table .el-table__body-wrapper td) {
  text-align: center !important;
}

::v-deep(.custom-scrollable-table .el-table__header-wrapper th:first-child),
::v-deep(.custom-scrollable-table .el-table__body-wrapper td:first-child),
::v-deep(.custom-scrollable-table .el-table__body-wrapper td:nth-child(2)) {
  text-align: left !important;
}

::v-deep(.custom-scrollable-table tbody tr td) {
  border-bottom: 0.5px solid black;
  border-right: 0.5px solid black;
}

::v-deep(.custom-scrollable-table thead tr th) {
  border-bottom: 0.5px solid black;
  border-right: 0.5px solid black;
}

::v-deep(.custom-scrollable-table thead tr th:last-child),
::v-deep(.custom-scrollable-table tbody tr td:last-child) {
  border-right: none;
}

@media (max-width: 768px) {
  ::v-deep(.custom-scrollable-table thead tr th:first-child),
  ::v-deep(.custom-scrollable-table tbody tr td:first-child) {
    filter: drop-shadow(4px 4px 10px rgba(0, 0, 0, 0.25));
  }
}

::v-deep(.el-table__body, .el-table__footer) {
  margin: 0 !important;
}

::v-deep(.el-table__header),
::v-deep(.el-table__body) {
  margin: 0 !important;
  width: 100% !important;
}

::v-deep(.dialog-color .el-dialog__header .el-dialog__headerbtn) {
  background: white !important;
}
</style>
